<template>
  <div id="app">
    <a-config-provider :locale="local">
      <!--    页面子路由-->
      <component :is="layout" />

      <!--    默认密码用户，修改默认密码-->
      <default-password-change />
      <!-- 切换企业弹窗 -->
      <company-change v-model="companyChangeDialog" />
      <!--    返回顶部-->
      <el-backtop :bottom="60" />
    </a-config-provider>

    <!--不同公司的主题色不同，在刷新时会出现闪动的情况，这里加一个白色不透明遮罩，进行隐藏-->
    <div v-if="maskVisible" class="mask"></div>

    <wl-supplier-lib-fee-notify v-if="inSupplierPlatform" />
  </div>
</template>

<script>
// layout组件
import NoLayout from '@/layout/NoLayout'
import DefaultLayout from '@/layout/index'
import NavBarLayout from '@/layout/NavBarLayout'
import BidOpenHallLayout from '@/views/hall/bid-open-hall/layout/BidOpenHallLayout'
import ReviewHallLayout from '@/views/hall/review-hall/layout/review-hall-layout'
// 组件
import DefaultPasswordChange from '@/components/common/AppComponent/DefaultPasswordChange'
import CompanyChange from '@/components/common/AppComponent/CompanyChange'
import WlSupplierLibFeeNotify from '@/views/home/components/WlSupplierLibFeeNotify.vue'
// 工具函数
import { mapState, mapActions, mapGetters } from 'vuex'
import { changeThemeColor } from '@/utils/theme'
import { verifyUserRuleHandel } from '@/utils/platform'
// 本地化
import zhCN from 'ant-design-vue/lib/locale-provider/zh_CN'
// 字典
import { workbenchServiceDict } from '@/dictionary/system'

export default {
  name: 'App',

  components: {
    // layout组件
    NoLayout,
    NavBarLayout,
    DefaultLayout,
    BidOpenHallLayout,
    ReviewHallLayout,

    // 普通组件
    DefaultPasswordChange,
    CompanyChange,

    // 特殊业务组件
    WlSupplierLibFeeNotify
  },

  // 将用户信息展示的函数引出
  provide() {
    return {
      showCompanyChange: this.showCompanyChange,
      showPersonSetting: this.showPersonSetting
    }
  },

  data() {
    return {
      // 是否显示企业切换
      companyChangeDialog: false,
      local: zhCN,

      // 未读消息提醒
      timer: null,

      maskVisible: true
    }
  },

  computed: {
    ...mapState('user', ['employeeStatus']),
    ...mapState('company', ['companyStatus', 'supervise', 'workbenchColor']),
    ...mapGetters('company', [
      'platformTitle',
      'loginAddress',
      'platformConfig'
    ]),
    ...mapState('router', ['currentWorkbench']),

    layout() {
      return this.$route.meta.layout || 'DefaultLayout'
    },

    // 是否处于供应商工作台
    inSupplierPlatform() {
      return this.currentWorkbench === workbenchServiceDict.SUPPLIER.value
    }
  },

  watch: {
    employeeStatus() {
      this.handleSuccessAndLogout()
    },
    companyStatus() {
      this.handleSuccessAndLogout()
    },

    // 设置平台主题颜色
    workbenchColor(val) {
      if (!val) {
        this.maskVisible = false
        return
      }

      changeThemeColor(val.split(','))
      this.maskVisible = false
    },

    // 设置平台名称
    platformTitle(val) {
      document && (document.title = val)
      this.changeFavicon()
    },

    // 监听路由的变化，并设置选中的一级路由
    $route: {
      handler: function(val) {
        verifyUserRuleHandel()
        const temp = val?.meta?.breadcrumb || []
        this.$store.commit(
          'router/SET_FIRST_LEVEL_ROUTE',
          temp[0]?.id || val?.meta?.id || 0
        )

        // 针对企业选择页面，去除遮罩，因为在企业选择页面无法获取主题色；无法监听workbenchColor
        if (val.name === 'SelectCompany') {
          this.maskVisible = false
        }
      },
      immediate: true,
      deep: true
    },

    // 监听平台的切换，请求未读消息
    currentWorkbench(val) {
      if (!val) return

      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }

      this.getNotice()
    }
  },

  beforeDestroy() {
    clearTimeout(this.timer)
    this.timer = null
  },

  methods: {
    ...mapActions('user', ['logout']),
    ...mapActions('system', ['getUnreadNotifyNumber']),

    /**
     * 修改平台的ico
     */
    changeFavicon() {
      const link = this.platformConfig.favicon

      let $favicon = document.querySelector('link[rel="icon"]')
      if (!link) {
        const $parent = $favicon.parentElement
        $parent.removeChild($favicon)
      }
      if ($favicon !== null) {
        $favicon.href = link
      } else {
        $favicon = document.createElement('link')
        $favicon.rel = 'icon'
        $favicon.href = link
        document.head.appendChild($favicon)
      }
    },

    /**
     * 处理用户登录成功和退出
     */
    handleSuccessAndLogout() {
      if (this.employeeStatus !== 0 && this.companyStatus !== 0) {
        return
      }

      this.$alert('当前登录用户已被禁用', '系统提示', {
        showClose: false,
        confirmButtonText: '确定',
        type: 'warning',
        callback: action => {
          this.logout()
            .then(res => {
              window.location.href = `${process.env.VUE_APP_PASSPORT_URL}`
            })
            .catch(err => {
              this.resetUser().then(res => {
                window.location.href = `${process.env.VUE_APP_PASSPORT_URL}`
              })
            })
        }
      })
    },

    // 显示切换企业弹窗
    showCompanyChange() {
      window.location.href = `${
        this.loginAddress
      }/select-company?returnUrl=${encodeURIComponent(window.location.href)}`
    },

    // 显示用户信息弹窗
    showPersonSetting() {
      const href = `${
        this.loginAddress
      }/personal-center?returnUrl=${encodeURIComponent(window.location.href)}`

      window.open(href, '_self')
    },

    /**
     * 每隔30s查询未读消息
     */
    getNotice() {
      this.getReadList()
      this.timer = setTimeout(() => {
        this.getNotice()
      }, 30000)
    },
    getReadList() {
      if (!this.currentWorkbench) return

      this.getUnreadNotifyNumber({ identity: this.currentWorkbench })
        .then(num => {
          const that = this
          // notice信息页不谈消息框
          const routerList = ['/notice']
          if (num > 0 && !routerList.includes(this.$route.path)) {
            this.noticeInstance = this.$notify({
              type: 'warning',
              title: '未读消息',
              message: '您有新消息，请注意及时查看!',
              offset: 100,
              onClick: () => {
                close()
                that.$router.push({ name: 'Notice' })
              }
            })
            const close = () => {
              this.noticeInstance.close()
            }
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped lang="scss">
#app {
  min-width: 1200px;

  .mask {
    position: fixed;
    width: 100%;
    height: 100vh;
    left: 0;
    top: 0;
    background-color: $white-color;
    z-index: 1000;
  }
}
</style>
